import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { UserContext } from "../../../UserContext";
import toast from "react-hot-toast";
import { FaArrowLeft, FaCalendarAlt, FaTimes } from "react-icons/fa";
import "./AddEditCrop.css";

const AddEditCrop = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [t, i18n] = useTranslation();
  const { token, userData } = useContext(UserContext);
  const fileInputRef = useRef(null);

  // Check if we're in edit mode
  const editMode = location.state?.editMode || false;
  const cropToEdit = location.state?.crop || null;
  console.log("crop to edit", cropToEdit);
  // Form state
  const [formData, setFormData] = useState({
    nameAr: cropToEdit?.nameAr || "",
    nameEn: cropToEdit?.nameEn || "",
    fieldId: cropToEdit?.fieldId || "",
    spanId: cropToEdit?.spanId || "", // Add spanId for hydroponics support
    inventoryTypeId: cropToEdit?.inventoryType?.id || cropToEdit?.typeId || "",
    inventoryLocationId: cropToEdit?.inventoryLocation?.id || cropToEdit?.locationId || "",
    initialBalance: cropToEdit?.initialBalance || cropToEdit?.currentBalance || "",
    price: cropToEdit?.price || "",
    unit: cropToEdit?.unit || "",
    date: cropToEdit?.procurementDate
      ? new Date(cropToEdit.procurementDate).toISOString().split("T")[0]
      : cropToEdit?.date
      ? new Date(cropToEdit.date).toISOString().split("T")[0]
      : "",
    details: cropToEdit?.details || "",
  });

  // Dropdown data
  const [fields, setFields] = useState([]);
  const [spans, setSpans] = useState([]); // Add spans for hydroponics
  const [inventoryTypes, setInventoryTypes] = useState([]);
  const [inventoryLocations, setInventoryLocations] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreview, setImagePreview] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [validationErrors, setValidationErrors] = useState({});

  const farmId = userData.Employee?.farmId;
  const farmType = userData.Employee?.farmType || "soil";
  const isArabic = i18n.language === "ar";

  // Fetch dropdown data
  useEffect(() => {
    const fetchDropdownData = async () => {
      if (!farmId) return;

      setLoadingData(true);
      try {
        // Fetch fields for soil farms OR spans for hydroponics farms
        if (farmType === "soil") {
          const fieldsResponse = await axios.get(
            `api/field/findByFarmId/${farmId}`,
            { headers: { Authorization: token } }
          );
          // Handle both array and object with items property
          const fieldsData = fieldsResponse.data;
          setFields(
            Array.isArray(fieldsData) ? fieldsData : fieldsData.items || []
          );
          console.log("field response:", fieldsResponse);
        } else if (farmType === "hydroponic" || farmType === "hydroponics") {
          // Fetch spans for hydroponics farms
          const spansResponse = await axios.get(
            `api/span/findByFarmId/${farmId}`,
            { headers: { Authorization: token } }
          );
          const spansData = spansResponse.data;
          setSpans(
            Array.isArray(spansData) ? spansData : spansData.items || []
          );
          console.log("spans response:", spansResponse);
        }

        // Fetch inventory types - using Android API endpoint
        const typesResponse = await axios.get(
          `api/inventoryType/findByFarmId/${farmId}`,
          { headers: { Authorization: token } }
        );
        // Handle both array and object with items property
        const typesData = typesResponse.data;
        setInventoryTypes(
          Array.isArray(typesData) ? typesData : typesData.items || []
        );

        // Fetch inventory locations - using Android API endpoint
        const locationsResponse = await axios.get(
          `api/inventoryLocation/findByFarmId/${farmId}`,
          { headers: { Authorization: token } }
        );
        console.log("types reponse:", typesResponse);
        console.log("location reponse:", locationsResponse);
        // Handle both array and object with items property
        const locationsData = locationsResponse.data;
        setInventoryLocations(
          Array.isArray(locationsData)
            ? locationsData
            : locationsData.items || []
        );
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
        toast.error(t("Failed to load data"));
      } finally {
        setLoadingData(false);
      }
    };

    fetchDropdownData();
  }, [farmId, farmType, token, t]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handle image selection
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);

    // Limit to 5 images
    if (selectedImages.length + files.length > 5) {
      toast.error(t("Maximum 5 images allowed"));
      return;
    }

    const newImages = [];
    const newPreviews = [];

    files.forEach((file) => {
      if (file.type.startsWith("image/")) {
        newImages.push(file);

        const reader = new FileReader();
        reader.onloadend = () => {
          newPreviews.push(reader.result);
          if (newPreviews.length === files.length) {
            setImagePreview((prev) => [...prev, ...newPreviews]);
          }
        };
        reader.readAsDataURL(file);
      }
    });

    setSelectedImages((prev) => [...prev, ...newImages]);
  };

  // Remove image
  const removeImage = (index) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
    setImagePreview((prev) => prev.filter((_, i) => i !== index));
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    const cropName = isArabic ? formData.nameAr : formData.nameEn;

    if (!cropName.trim()) {
      errors.name = t("Crop name is required");
    }

    // CRITICAL: Field is MANDATORY for crops in soil farms
    if (farmType === "soil" && !formData.fieldId) {
      errors.field = t("Field selection is required for crops");
    }

    // CRITICAL: Span is MANDATORY for crops in hydroponics farms
    if ((farmType === "hydroponic" || farmType === "hydroponics") && !formData.spanId) {
      errors.span = t("Span selection is required for hydroponics crops");
    }

    if (!formData.inventoryTypeId) {
      errors.type = t("Type selection is required");
    }

    if (!formData.inventoryLocationId) {
      errors.warehouse = t("Warehouse selection is required");
    }

    // Initial balance only required for new crops
    if (!editMode && (!formData.initialBalance || formData.initialBalance <= 0)) {
      errors.initialBalance = t("Initial balance is required");
    }

    // Price only required for new crops (for procurement)
    if (!editMode && (!formData.price || formData.price <= 0)) {
      errors.price = t("Price is required");
    }

    if (!formData.unit.trim()) {
      errors.unit = t("Unit is required");
    }

    // Date only required for new crops (for procurement)
    if (!editMode && !formData.date) {
      errors.date = t("Date is required");
    }

    setValidationErrors(errors);

    // Log validation errors for debugging
    if (Object.keys(errors).length > 0) {
      console.error("Validation errors:", errors);
    }

    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      // Show specific error messages
      const errorMessages = Object.values(validationErrors).join(", ");
      toast.error(errorMessages || t("Please fill all required fields"));
      return;
    }

    // Double-check fieldId/spanId for crops (critical validation)
    if (farmType === "soil" && !formData.fieldId) {
      toast.error(t("Field selection is mandatory for crops!"));
      return;
    }
    if ((farmType === "hydroponic" || farmType === "hydroponics") && !formData.spanId) {
      toast.error(t("Span selection is mandatory for hydroponics crops!"));
      return;
    }

    setLoading(true);
    try {
      const cropName = isArabic ? formData.nameAr : formData.nameEn;

      // Match backend field names (matching Android ProductMapper)
      const payload = {
        nameAr: formData.nameAr || cropName,
        nameEn: formData.nameEn || cropName,
        tenantId: userData.Employee?.tenantId || userData.User?.id,
        farmId: farmId,
        typeId: parseInt(formData.inventoryTypeId),
        unit: formData.unit,
        details: formData.details,
      };

      // Add fieldId for soil farms OR spanId for hydroponics (matching Android logic)
      if (farmType === "soil") {
        payload.fieldId = formData.fieldId ? parseInt(formData.fieldId) : null;
        // Final validation: crops MUST have fieldId for soil
        if (!editMode && !payload.fieldId) {
          console.error("CRITICAL: Attempting to save crop without fieldId!");
          toast.error(t("Field is required for crops"));
          return;
        }
      } else if (farmType === "hydroponic" || farmType === "hydroponics") {
        payload.spanId = formData.spanId ? parseInt(formData.spanId) : null;
        // Final validation: crops MUST have spanId for hydroponics
        if (!editMode && !payload.spanId) {
          console.error("CRITICAL: Attempting to save crop without spanId!");
          toast.error(t("Span is required for hydroponics crops"));
          return;
        }
      }

      // Include locationId, initialBalance, threshold for crops being added to stock
      // (matching Android ProductMapper line 138-141: if (addToStock || isCrop))
      if (!editMode && formData.initialBalance && formData.inventoryLocationId) {
        payload.locationId = parseInt(formData.inventoryLocationId);
        payload.initialBalance = parseFloat(formData.initialBalance);
        payload.threshold = null; // Optional: can add threshold field later
      }

      console.log("Saving crop with payload:", payload);

      let response;

      // Always use multipart endpoint for consistency
      const multipartData = new FormData();

      // Add product data as JSON string
      if (editMode) {
        payload.id = cropToEdit.id;
      }

      // Create a proper JSON blob (matching Android implementation)
      const productBlob = new Blob([JSON.stringify(payload)], {
        type: "application/json; charset=utf-8",
      });
      multipartData.append("product", productBlob);

      // Log for debugging
      console.log("Sending multipart data with fieldId:", payload.fieldId);

      // Add image files if any
      if (selectedImages.length > 0) {
        selectedImages.forEach((image) => {
          multipartData.append("imageFile", image);
        });
      }

      if (editMode) {
        // Update product
        response = await axios.put(
          "api/inventoryItemDetails/updateProductWithImages",
          multipartData,
          {
            headers: {
              Authorization: token,
              "Content-Type": "multipart/form-data",
            },
          }
        );
      } else {
        // Add new product
        response = await axios.post(
          "api/inventoryItemDetails/addProductWithImages",
          multipartData,
          {
            headers: {
              Authorization: token,
              "Content-Type": "multipart/form-data",
            },
          }
        );
      }

      // Check if response indicates success
      if (
        response.data &&
        (response.data.status === 200 ||
          response.data.items ||
          response.data.id)
      ) {
        // If adding new crop (not editing), create procurement entry
        if (!editMode && response.data.items && response.data.items[0]) {
          const createdProduct = response.data.items[0];

          // Create procurement entry to add initial balance to stock
          const procurementPayload = {
            inventoryItemDetailId: createdProduct.id,
            tenantId: userData.Employee?.tenantId || userData.User?.id,
            farmId: farmId,
            quantity: parseFloat(formData.initialBalance),
            price: parseFloat(formData.price),
            procurementDate: new Date(formData.date).getTime(),
            locationId: parseInt(formData.inventoryLocationId),
          };

          try {
            await axios.post("api/inventoryProcurement", procurementPayload, {
              headers: { Authorization: token },
            });
          } catch (procurementError) {
            console.error(
              "Failed to create procurement entry:",
              procurementError
            );
            // Don't fail the whole operation if procurement fails
            toast.warning(t("Product created but initial stock entry failed"));
          }
        }

        toast.success(
          t(editMode ? "Crop updated successfully" : "Crop added successfully")
        );
        navigate("/inventory/stock-products");
      } else {
        throw new Error(response.data?.message || "Operation failed");
      }
    } catch (error) {
      console.error("Error saving crop:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        t(editMode ? "Failed to update crop" : "Failed to add crop");
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Get display name for dropdowns
  const getDisplayName = (item) => {
    if (!item) return "";
    return isArabic
      ? item.nameAr || item.nameEn || item.name
      : item.nameEn || item.nameAr || item.name;
  };

  if (loadingData) {
    return (
      <div className="cae-add-container">
        <div className="cae-loading-spinner">{t("Loading...")}</div>
      </div>
    );
  }

  return (
    <div className="cae-add-container">
      <div className="cae-form-card">
        {/* Header */}
        <div className="cae-form-header">
          <button
            className="cae-back-button"
            onClick={() => navigate(-1)}
            type="button"
            aria-label={t("Go back")}
          >
            <FaArrowLeft />
          </button>
          <div className="cae-form-header-content">
            <h1>{editMode ? t("Edit Crop") : t("Add Crop")}</h1>
            <p>
              {editMode
                ? t("Modify crop information")
                : t("Add new harvest to inventory")}
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="cae-form-body">
            {/* Basic Information Section */}
            <div className="cae-form-section">
              <h2 className="cae-section-title">{t("Basic Information")}</h2>

              {/* Crop Name */}
              <div className="cae-form-row">
                <div
                  className={`cae-form-group ${
                    validationErrors.name ? "error" : ""
                  }`}
                >
                  <label htmlFor="cropNameAr">
                    {t("Crop Name (Arabic)")}{" "}
                    <span className="cae-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="cropNameAr"
                    name="nameAr"
                    value={formData.nameAr}
                    onChange={handleInputChange}
                    placeholder={t("Enter crop name in Arabic")}
                  />
                </div>

                <div
                  className={`cae-form-group ${
                    validationErrors.name ? "error" : ""
                  }`}
                >
                  <label htmlFor="cropNameEn">
                    {t("Crop Name (English)")}{" "}
                    <span className="cae-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="cropNameEn"
                    name="nameEn"
                    value={formData.nameEn}
                    onChange={handleInputChange}
                    placeholder={t("Enter crop name in English")}
                  />
                  {validationErrors.name && (
                    <span className="cae-error-message">
                      {validationErrors.name}
                    </span>
                  )}
                </div>
              </div>

              {/* Field/Span and Type */}
              <div className="cae-form-row">
                {/* Show Field selector for soil farms */}
                {farmType === "soil" && (
                  <div
                    className={`cae-form-group ${
                      validationErrors.field ? "error" : ""
                    }`}
                  >
                    <label htmlFor="fieldId">
                      {t("Field")} <span className="cae-required">*</span>
                    </label>
                    <select
                      id="fieldId"
                      name="fieldId"
                      value={formData.fieldId}
                      onChange={handleInputChange}
                    >
                      <option value="">{t("Select Field")}</option>
                      {fields.map((field) => (
                        <option key={field.id} value={field.id}>
                          {getDisplayName(field)}
                        </option>
                      ))}
                    </select>
                    {validationErrors.field && (
                      <span className="cae-error-message">
                        {validationErrors.field}
                      </span>
                    )}
                  </div>
                )}

                {/* Show Span selector for hydroponics farms */}
                {(farmType === "hydroponic" || farmType === "hydroponics") && (
                  <div
                    className={`cae-form-group ${
                      validationErrors.span ? "error" : ""
                    }`}
                  >
                    <label htmlFor="spanId">
                      {t("Span")} <span className="cae-required">*</span>
                    </label>
                    <select
                      id="spanId"
                      name="spanId"
                      value={formData.spanId}
                      onChange={handleInputChange}
                    >
                      <option value="">{t("Select Span")}</option>
                      {spans.map((span) => (
                        <option key={span.id} value={span.id}>
                          {getDisplayName(span)}
                        </option>
                      ))}
                    </select>
                    {validationErrors.span && (
                      <span className="cae-error-message">
                        {validationErrors.span}
                      </span>
                    )}
                  </div>
                )}

                <div
                  className={`cae-form-group ${validationErrors.type ? "error" : ""}`}
                >
                  <label htmlFor="inventoryTypeId">
                    {t("Type")} <span className="cae-required">*</span>
                  </label>
                  <select
                    id="inventoryTypeId"
                    name="inventoryTypeId"
                    value={formData.inventoryTypeId}
                    onChange={handleInputChange}
                  >
                    <option value="">{t("Select Type")}</option>
                    {inventoryTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {getDisplayName(type)}
                      </option>
                    ))}
                  </select>
                  {validationErrors.type && (
                    <span className="cae-error-message">
                      {validationErrors.type}
                    </span>
                  )}
                </div>
              </div>

              {/* Warehouse */}
              <div className="cae-form-row">
                <div
                  className={`cae-form-group full-width ${
                    validationErrors.warehouse ? "error" : ""
                  }`}
                >
                  <label htmlFor="inventoryLocationId">
                    {t("Warehouse")} <span className="cae-required">*</span>
                  </label>
                  <select
                    id="inventoryLocationId"
                    name="inventoryLocationId"
                    value={formData.inventoryLocationId}
                    onChange={handleInputChange}
                  >
                    <option value="">{t("Select Warehouse")}</option>
                    {inventoryLocations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {getDisplayName(location)}
                      </option>
                    ))}
                  </select>
                  {validationErrors.warehouse && (
                    <span className="cae-error-message">
                      {validationErrors.warehouse}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Stock Information Section */}
            <div className="cae-form-section">
              <h2 className="cae-section-title">{t("Stock Information")}</h2>

              {/* Balance and Unit */}
              <div className="cae-form-row">
                {!editMode && (
                  <div
                    className={`cae-form-group ${
                      validationErrors.initialBalance ? "error" : ""
                    }`}
                  >
                    <label htmlFor="initialBalance">
                      {t("Initial Balance")}{" "}
                      <span className="cae-required">*</span>
                    </label>
                    <input
                      type="number"
                      id="initialBalance"
                      name="initialBalance"
                      value={formData.initialBalance}
                      onChange={handleInputChange}
                      placeholder={t("0.00")}
                      step="0.01"
                      min="0"
                    />
                    {validationErrors.initialBalance && (
                      <span className="cae-error-message">
                        {validationErrors.initialBalance}
                      </span>
                    )}
                  </div>
                )}

                <div
                  className={`cae-form-group ${
                    editMode ? "full-width" : ""
                  } ${
                    validationErrors.unit ? "error" : ""
                  }`}
                >
                  <label htmlFor="unit">
                    {t("Unit")} <span className="cae-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="unit"
                    name="unit"
                    value={formData.unit}
                    onChange={handleInputChange}
                    placeholder={t("e.g., kg, ton, piece")}
                  />
                  {validationErrors.unit && (
                    <span className="cae-error-message">
                      {validationErrors.unit}
                    </span>
                  )}
                </div>
              </div>

              {/* Price and Date - Only show in create mode */}
              {!editMode && (
                <div className="cae-form-row">
                  <div
                    className={`cae-form-group ${
                      validationErrors.price ? "error" : ""
                    }`}
                  >
                    <label htmlFor="price">
                      {t("Price")} <span className="cae-required">*</span>
                    </label>
                    <input
                      type="number"
                      id="price"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      placeholder={t("0.00")}
                      step="0.01"
                      min="0"
                    />
                    {validationErrors.price && (
                      <span className="cae-error-message">
                        {validationErrors.price}
                      </span>
                    )}
                  </div>

                  <div
                    className={`cae-form-group ${
                      validationErrors.date ? "error" : ""
                    }`}
                  >
                    <label htmlFor="date">
                      {t("Date")} <span className="cae-required">*</span>
                    </label>
                    <div className="cae-date-input-wrapper">
                      <input
                        type="date"
                        id="date"
                        name="date"
                        value={formData.date}
                        onChange={handleInputChange}
                        max={new Date().toISOString().split("T")[0]}
                      />
                      <FaCalendarAlt className="cae-date-icon" />
                    </div>
                    {validationErrors.date && (
                      <span className="cae-error-message">
                        {validationErrors.date}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Additional Details Section */}
            <div className="cae-form-section">
              <h2 className="cae-section-title">{t("Additional Details")}</h2>

              {/* Description */}
              <div className="cae-form-group full-width">
                <label htmlFor="details">{t("Description")}</label>
                <textarea
                  id="details"
                  name="details"
                  value={formData.details}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder={t("Enter additional details about the crop...")}
                />
              </div>

              {/* Images */}
              <div className="cae-form-group full-width">
                <label>{t("Crop Images")}</label>
                <div className="cae-image-upload-section">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageSelect}
                    accept="image/*"
                    multiple
                    id="cae-image-upload"
                  />
                  <label
                    htmlFor="cae-image-upload"
                    className="cae-image-upload-label"
                  >
                    {t("Choose Images")}
                  </label>
                  <p className="cae-upload-hint">
                    {t("Upload up to 5 images")}
                  </p>
                </div>

                {imagePreview.length > 0 && (
                  <div className="cae-image-preview">
                    {imagePreview.map((preview, index) => (
                      <div key={index} className="cae-image-item">
                        <img src={preview} alt={`Preview ${index + 1}`} />
                        <p className="cae-image-name">
                          {selectedImages[index]?.name}
                        </p>
                        <button
                          type="button"
                          className="cae-remove-image-button"
                          onClick={() => removeImage(index)}
                          aria-label={t("Remove image")}
                        >
                          <FaTimes />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="cae-form-actions">
            <button
              type="button"
              className="cae-button-secondary"
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              {t("Cancel")}
            </button>
            <button
              type="submit"
              className="cae-button-primary"
              disabled={loading}
            >
              {loading
                ? t("Saving...")
                : editMode
                ? t("Update Crop")
                : t("Save Crop")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditCrop;
